import { useState, useRef, useCallback } from 'react'
import { Upload, FileText, Loader2, FolderOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/utils'
import { useCsvImports, useUploadCsv } from '@/hooks/useOutreach'
import type { CsvImportRecord } from '@/types/outreach'

function statusColor(status: string): string {
  switch (status) {
    case 'completed': return 'text-green-500 bg-green-500/10'
    case 'processing': return 'text-amber-500 bg-amber-500/10'
    case 'failed': return 'text-red-500 bg-red-500/10'
    default: return 'text-muted-foreground bg-muted'
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'completed': return 'Concluido'
    case 'processing': return 'Processando...'
    case 'failed': return 'Falhou'
    case 'pending': return 'Pendente'
    default: return status
  }
}

function Stat({ label, value, colorClass }: { label: string; value: number; colorClass?: string }) {
  return (
    <div className="text-center">
      <p className="text-lg font-bold">{value}</p>
      <p className={cn('text-[10px] font-medium', colorClass || 'text-muted-foreground')}>{label}</p>
    </div>
  )
}

export default function CsvImportPage() {
  const imports = useCsvImports()
  const upload = useUploadCsv()
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) return
    upload.mutate(file, {
      onSuccess: () => {
        imports.refetch()
      },
    })
  }, [upload, imports])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragging(false)
  }, [])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = ''
  }, [handleFile])

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Importar CSV</h1>
        <p className="text-muted-foreground mt-1">Importe contatos do LinkedIn Sales Navigator</p>
      </div>

      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          'flex flex-col items-center justify-center gap-3 py-10 border-2 border-dashed rounded-xl cursor-pointer transition-colors',
          dragging ? 'border-primary bg-primary/5' : 'border-primary/40 hover:border-primary/60 hover:bg-accent/30',
          upload.isPending && 'pointer-events-none opacity-60'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleInputChange}
          className="hidden"
        />
        {upload.isPending ? (
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        ) : (
          <>
            <Upload className="w-8 h-8 text-primary" />
            <p className="text-sm font-semibold text-primary">Importar CSV do Sales Navigator</p>
            <p className="text-xs text-muted-foreground text-center px-6">
              Arraste e solte um arquivo .csv ou clique para selecionar
            </p>
          </>
        )}
      </div>

      {upload.isError && (
        <p className="text-sm text-destructive">Falha ao enviar CSV. Tente novamente.</p>
      )}
      {upload.isSuccess && (
        <p className="text-sm text-green-500">CSV enviado! Processamento em background.</p>
      )}

      {/* Import History */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Historico de Importacoes</h2>

        {imports.isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : imports.data?.items?.length ? (
          <div className="space-y-3">
            {imports.data.items.map((item) => (
              <div key={item.id} className="bg-card border rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium truncate flex-1">{item.filename}</span>
                  <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full', statusColor(item.status))}>
                    {statusLabel(item.status)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <Stat label="Linhas" value={item.total_rows} />
                  <Stat label="Novos" value={item.new_leads} colorClass="text-green-500" />
                  <Stat label="Contatos" value={item.new_contacts} colorClass="text-blue-500" />
                  <Stat label="Atualizados" value={item.updated_contacts} colorClass="text-amber-500" />
                  <Stat label="Ignorados" value={item.skipped_rows} colorClass="text-red-500" />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {new Date(item.created_at).toLocaleDateString('pt-BR')}{' '}
                  {new Date(item.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-card border rounded-xl p-12 text-center">
            <FolderOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-semibold">Nenhuma importacao realizada</h3>
            <p className="text-sm text-muted-foreground mt-1">Importe um CSV para comecar.</p>
          </div>
        )}
      </div>
    </div>
  )
}
